import { assign, fromPromise, setup } from "xstate";
import type { AbstractPowerSyncDatabase } from "@powersync/common";

// --- Types ---

export type SignOutEvent =
  | { type: "SIGN_OUT_REQUESTED" }
  | { type: "CLEAR_DATA_REQUESTED" }
  | { type: "REVOKE_REQUESTED"; sessionId: string }
  | { type: "FORCE_LOGOUT_REQUESTED" }
  | { type: "LOGOUT_QUICK_REQUESTED" }
  | { type: "CANCELLED" }
  | { type: "CONFIRMED" };

export interface SignOutInput {
  db: AbstractPowerSyncDatabase | null;
  disconnectAndClear: () => Promise<void>;
  session: unknown; // Minimal session type or unknown to avoid coupling
  onRevokeSuccess?: () => void;
  /**
   * P23 / CC-60 · phase 2 (SECURITY): invoked when revocation FAILS.
   * Without this, "I removed the device I lost" was announced as successful
   * even if the server had rejected it. See `revokeSessionActor`.
   */
  onRevokeError?: (error: unknown) => void;
  onSignOut?: () => Promise<void>;
  onRedirectToSignIn: () => void;
  onRevokeSession: (sessionId: string) => Promise<void>;
}

// --- Machine ---

export interface SignOutContext {
  revokeSessionId: string | null;
  db: AbstractPowerSyncDatabase | null;
  disconnectAndClear: () => Promise<void>;
  session: unknown;
  hasUnsyncedChanges: boolean;
  onRevokeSuccess?: () => void;
  onRevokeError?: (error: unknown) => void;
  onSignOut?: () => Promise<void>;
  onRedirectToSignIn: () => void;
  onRevokeSession: (sessionId: string) => Promise<void>;
}

export const signOutMachine = setup({
  // Schema Definition Pattern for XState v5 type inference
  // oxlint-disable-next-line typescript/no-unsafe-type-assertion
  types: {} as {
    context: SignOutContext;
    input: SignOutInput;
    events: SignOutEvent;
  },
  actors: {
    checkUnsyncedActor: fromPromise(
      async ({ input }: { input: { db: AbstractPowerSyncDatabase | null } }): Promise<boolean> => {
        // Race against a timeout to prevent hanging the UI if DB is stuck
        const CHECK_TIMEOUT_MS = 2000;

        const checkPromise = (async () => {
          try {
            if (!input.db) {
              return false;
            }
            const transaction = await input.db.getNextCrudTransaction();
            return Boolean(transaction);
          } catch (error) {
            console.error("[Auth] Failed to check for unsynced changes:", error);
            return false;
          }
        })();

        const timeoutPromise = new Promise<boolean>((resolve) => {
          setTimeout(() => {
            console.warn("[Auth] Unsynced check timed out, proceeding with default flow.");
            resolve(false);
          }, CHECK_TIMEOUT_MS);
        });

        return Promise.race([checkPromise, timeoutPromise]);
      },
    ),
    executeSignOutActor: fromPromise(
      async ({
        input,
      }: {
        input: {
          force: boolean;
          disconnectAndClear: () => Promise<void>;
          onSignOut?: () => Promise<void>;
          onRedirectToSignIn: () => void;
        };
      }) => {
        const EXECUTE_TIMEOUT_MS = 3000;

        const signOutTask = (async () => {
          try {
            // Initiate heavy cleanup in background (non-blocking for the UI)
            void (async () => {
              try {
                await input.disconnectAndClear();
              } catch (err) {
                console.warn("[Auth] PowerSync background cleanup failed:", err);
              }
            })();

            // Await the authentication sign-out
            if (input.onSignOut) {
              await input.onSignOut();
            }
          } catch (error) {
            console.error("[Auth] Sign out task failed:", error);
          }
        })();

        const timeoutPromise = new Promise<void>((resolve) => {
          setTimeout(() => {
            console.warn("[Auth] Sign out timed out, proceeding to redirect.");
            resolve();
          }, EXECUTE_TIMEOUT_MS);
        });

        return Promise.race([signOutTask, timeoutPromise]);
      },
    ),
    clearDataActor: fromPromise(
      async ({
        input,
      }: {
        input: {
          session: unknown;
          disconnectAndClear: () => Promise<void>;
          onRedirectToSignIn: () => void;
        };
      }) => {
        try {
          if (input.session) {
            input.onRedirectToSignIn();
          }

          // Background cleanup
          // We trigger it but don't await it here to avoid blocking the UI/Actor transition.
          void (async () => {
            try {
              await input.disconnectAndClear();
              console.info("[Profile] Local data clear completed in background.");
            } catch (error) {
              console.error("[Profile] Failed to clear local data in background:", error);
            }
          })();
        } catch (error) {
          console.error("[Profile] Failed to clear local data actor:", error);
        }
        // Satisfy lint: async function must have await, and promise-returning function must be async
        await Promise.resolve();
      },
    ),
    /**
     * P23 / CC-60 · phase 2 — SECURITY: there used to be a `try/catch` here
     * that swallowed the error and returned success. Combined with
     * `onDone → clear + callOnRevokeSuccess`, the machine announced a
     * SUCCESSFUL revocation that the server had REJECTED: someone removing a
     * lost device was told it had worked. Measured in this repo with a forced
     * 500 on `/api/auth/revoke-session`: the dialog closed, there were zero
     * warnings, the list stayed intact, and the other device remained inside.
     *
     * Now the error PROPAGATES → the machine takes `onError` and the consumer
     * can tell the truth (`onRevokeError`).
     */
    revokeSessionActor: fromPromise(
      async ({
        input,
      }: {
        input: { sessionId: string; onRevokeSession: (sessionId: string) => Promise<void> };
      }) => {
        await input.onRevokeSession(input.sessionId);
      },
    ),
  },
  actions: {
    setRevokeSessionId: ({ context, event }) => {
      if (event.type === "REVOKE_REQUESTED") {
        context.revokeSessionId = event.sessionId;
      }
    },
    clearRevokeSessionId: ({ context }) => {
      context.revokeSessionId = null;
    },
    callOnRevokeSuccess: ({ context }) => {
      context.onRevokeSuccess?.();
    },
    callOnRevokeError: ({ context, event }) => {
      // The `onError` event (`xstate.error.actor.*`) carries the error in
      // `event.error`; it is not in the `SignOutEvent` union, so it is checked
      // with `in` instead of an assertion.
      context.onRevokeError?.("error" in event ? event.error : undefined);
    },
    callRedirect: ({ context }) => {
      context.onRedirectToSignIn();
    },
  },
}).createMachine({
  id: "signOut",
  initial: "idle",
  context: ({ input }) => ({
    revokeSessionId: null,
    db: input.db,
    disconnectAndClear: input.disconnectAndClear,
    session: input.session,
    hasUnsyncedChanges: false,
    onRevokeSuccess: input.onRevokeSuccess,
    onRevokeError: input.onRevokeError,
    onSignOut: input.onSignOut,
    onRedirectToSignIn: input.onRedirectToSignIn,
    onRevokeSession: input.onRevokeSession,
  }),
  states: {
    idle: {
      on: {
        SIGN_OUT_REQUESTED: "checkingUnsynced",
        CLEAR_DATA_REQUESTED: "confirmClear",
        REVOKE_REQUESTED: {
          target: "confirmRevoke",
          actions: "setRevokeSessionId",
        },
        FORCE_LOGOUT_REQUESTED: "executingForce",
        LOGOUT_QUICK_REQUESTED: {
          target: "confirmSignOut",
          actions: assign({
            hasUnsyncedChanges: () => false,
          }),
        },
      },
    },

    checkingUnsynced: {
      invoke: {
        src: "checkUnsyncedActor",
        input: ({ context }) => ({ db: context.db }),
        // If the user has CRUD changes still queued locally, prompt them
        // first with a "you will lose data" confirmation (→ confirmDataLoss
        // → executingForce). Otherwise fall through to the normal sign-out
        // confirmation. Errors fall through to the safe "clean sign-out"
        // branch — the user can retry later if anything was actually queued.
        onDone: [
          {
            guard: ({ event }) => event.output,
            target: "confirmDataLoss",
            actions: assign({
              hasUnsyncedChanges: () => true,
            }),
          },
          {
            target: "confirmSignOut",
            actions: assign({
              hasUnsyncedChanges: () => false,
            }),
          },
        ],
        onError: {
          target: "confirmSignOut",
          actions: assign({
            hasUnsyncedChanges: () => false,
          }),
        },
      },
      on: {
        CANCELLED: "idle",
      },
    },

    confirmSignOut: {
      on: {
        CONFIRMED: "executing",
        CANCELLED: "idle",
        FORCE_LOGOUT_REQUESTED: "executingForce",
      },
    },

    /**
     * Surfaced when `checkingUnsynced` finds local CRUD changes that haven't
     * been uploaded yet. Confirming here is a destructive force-logout that
     * wipes the local DB without waiting for the queue to drain. Cancelling
     * returns to `idle` so the user can keep using the app until sync catches
     * up.
     */
    confirmDataLoss: {
      on: {
        CONFIRMED: "executingForce",
        CANCELLED: "idle",
      },
    },

    confirmClear: {
      on: {
        CONFIRMED: "clearing",
        CANCELLED: "idle",
      },
    },

    confirmRevoke: {
      on: {
        CONFIRMED: "revoking",
        CANCELLED: {
          target: "idle",
          actions: "clearRevokeSessionId",
        },
      },
    },

    executing: {
      entry: "callRedirect",
      invoke: {
        src: "executeSignOutActor",
        input: ({ context }) => ({
          force: false,
          disconnectAndClear: context.disconnectAndClear,
          onSignOut: context.onSignOut,
          onRedirectToSignIn: () => {
            /* empty */
          }, // Handled by entry action
        }),
        onDone: "idle",
        onError: "idle",
      },
    },

    executingForce: {
      entry: "callRedirect",
      invoke: {
        src: "executeSignOutActor",
        input: ({ context }) => ({
          force: true,
          disconnectAndClear: context.disconnectAndClear,
          onSignOut: context.onSignOut,
          onRedirectToSignIn: () => {
            /* empty */
          }, // Handled by entry action
        }),
        onDone: "idle",
        onError: "idle",
      },
    },

    clearing: {
      invoke: {
        src: "clearDataActor",
        input: ({ context }) => ({
          session: context.session,
          disconnectAndClear: context.disconnectAndClear,
          onRedirectToSignIn: context.onRedirectToSignIn,
        }),
        onDone: "idle",
        onError: "idle",
      },
    },

    revoking: {
      invoke: {
        src: "revokeSessionActor",
        input: ({ context }) => ({
          sessionId: context.revokeSessionId ?? "",
          onRevokeSession: context.onRevokeSession,
        }),
        onDone: {
          target: "idle",
          actions: ["clearRevokeSessionId", "callOnRevokeSuccess"],
        },
        // `onRevokeSuccess` is NOT called here: success must be announced only when
        // it actually happened; the consumer reports failure with `onRevokeError`.
        onError: {
          target: "idle",
          actions: ["clearRevokeSessionId", "callOnRevokeError"],
        },
      },
    },
  },
});
/**
 * Helper to determine if a sign-out or cleanup process is currently executing.
 */
export function isSignOutInProgress(state: { value: string }): boolean {
  return (
    state.value === "executing" ||
    state.value === "executingForce" ||
    state.value === "clearing" ||
    state.value === "revoking"
  );
}
